// Holds the cart whose order is already placed. The app-switch return remounts the dropin without
// tearing the old one down, so two instances poll at once and an in-memory latch, private to one
// closure, can't stop the loser painting an error over a completed order.
export const PAYMENT_COMPLETED_STORAGE_KEY = "adyen_payment_completed";
// Holds the qrCode action so a reloaded document can rebuild the QR instead of returning the
// shopper to the pay button. Without it the only way forward is a second payment attempt, which
// double-charges when the first one was already authorised while the shopper was in the app.
export const PENDING_QR_ACTION_STORAGE_KEY = "adyen_pending_qr_action";

export type PlacedOrder = {
    orderNumber: string;
    orderDraftId?: string | null;
    paymentMethod?: string;
};

/** UPI (upi, upi_qr, upi_collect, upi_intent): HEAP places its order before the shopper pays. */
export const isUpiPaymentMethod = (type?: string): boolean => !!type && /^upi(_|$)/.test(type);

export type PendingPlacedOrder = {
    cartId: string;
    placedOrder: PlacedOrder;
};

/** The UPI order HEAP placed before its QR was paid, if it is still waiting. */
export const readPendingPlacedOrder = (): PendingPlacedOrder | undefined => {
    try {
        const stored = sessionStorage.getItem(PENDING_QR_ACTION_STORAGE_KEY);
        const parsed = stored
            ? (JSON.parse(stored) as Partial<PendingPlacedOrder> | null)
            : undefined;
        return parsed?.cartId && parsed.placedOrder?.orderNumber
            ? { cartId: parsed.cartId, placedOrder: parsed.placedOrder }
            : undefined;
    } catch {
        // No sessionStorage on the server, or an unreadable entry.
        return undefined;
    }
};

export const clearPendingPlacedOrder = (): void => {
    try {
        sessionStorage.removeItem(PENDING_QR_ACTION_STORAGE_KEY);
    } catch {
        // No sessionStorage on the server.
    }
};

/** The cart of the last completed payment on this page. */
export const readCompletedPaymentCartId = (): string | undefined => {
    try {
        return sessionStorage.getItem(PAYMENT_COMPLETED_STORAGE_KEY) ?? undefined;
    } catch {
        return undefined;
    }
};
